import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Heart, Calendar, Plus, Trash2, Clock, Star, Eye, EyeOff, Upload, Camera, CreditCard, CheckCircle, AlertTriangle, X, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HealerDashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [healerTypes, setHealerTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReviews, setShowReviews] = useState(true);
  const [healerReviews, setHealerReviews] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);

  // Subscription
  const [subscription, setSubscription] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Profile dialog
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [profileForm, setProfileForm] = useState({
    healer_type: '',
    bio: '',
    specialties: '',
    languages: '',
    insurance_accepted: '',
    location: '',
    hourly_rate: '',
    years_experience: '',
    profile_image: '',
    credentials: '',
    blog_url: '',
    practice_quote: '',
    accolades: '',
    contact_email: '',
    contact_phone: '',
    contact_website: '',
    contact_instagram: '',
    ethnicity: ''
  });

  // Availability dialog
  const [showAvailabilityDialog, setShowAvailabilityDialog] = useState(false);
  const [availabilityForm, setAvailabilityForm] = useState({
    date: '',
    start_time: '',
    end_time: ''
  });

  useEffect(() => {
    fetchHealerTypes();
    fetchProfile();
    fetchBookings();
    fetchAvailability();
    fetchHealerReviews();
    fetchSubscription();

    // Check for returning from Stripe
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchHealerTypes = async () => {
    try {
      const response = await axios.get(`${API}/healer-types`);
      setHealerTypes(response.data);
    } catch (error) {
      console.error('Error fetching healer types:', error);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/healers/${user.id}`);
      setProfile(response.data);
      setShowReviews(response.data.show_reviews !== false);
      // Populate form
      setProfileForm({
        healer_type: response.data.healer_type || '',
        bio: response.data.bio || '',
        specialties: response.data.specialties?.join(', ') || '',
        languages: response.data.languages?.join(', ') || '',
        insurance_accepted: response.data.insurance_accepted?.join(', ') || '',
        location: response.data.location || '',
        hourly_rate: response.data.hourly_rate || '',
        years_experience: response.data.years_experience || '',
        profile_image: response.data.profile_image || '',
        credentials: response.data.credentials || '',
        blog_url: response.data.blog_url || '',
        practice_quote: response.data.practice_quote || '',
        accolades: response.data.accolades || '',
        contact_email: response.data.contact_email || '',
        contact_phone: response.data.contact_phone || '',
        contact_website: response.data.contact_website || '',
        contact_instagram: response.data.contact_instagram || '',
        ethnicity: response.data.ethnicity || ''
      });
    } catch (error) {
      console.log('No profile found');
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  const fetchAvailability = async () => {
    try {
      const response = await axios.get(`${API}/availability/${user.id}`);
      setAvailability(response.data);
    } catch (error) {
      console.error('Error fetching availability:', error);
    }
  };

  const fetchHealerReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/healer/${user.id}`);
      setHealerReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const toggleReviewVisibility = async () => {
    try {
      const response = await axios.put(`${API}/healers/profile/reviews-visibility`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowReviews(response.data.show_reviews);
      toast.success(`Reviews ${response.data.show_reviews ? 'visible' : 'hidden'} on your profile`);
    } catch (error) {
      toast.error('Failed to update review settings');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, or WebP image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/profile-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Profile image uploaded!');
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const currentCount = profile?.photos?.length || 0;
    const remaining = 10 - currentCount;
    if (remaining <= 0) {
      toast.error('Gallery is full (max 10 photos). Delete one to add more.');
      e.target.value = '';
      return;
    }
    const toUpload = files.slice(0, remaining);
    if (files.length > remaining) {
      toast.info(`Only uploading first ${remaining} photo(s). Gallery limit is 10.`);
    }

    setUploadingGallery(true);
    let successCount = 0;
    for (const file of toUpload) {
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        toast.error(`Skipped ${file.name}: must be JPEG, PNG, or WebP`);
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`Skipped ${file.name}: must be under 5MB`);
        continue;
      }
      const formData = new FormData();
      formData.append('file', file);
      try {
        await axios.post(`${API}/upload/gallery-photo`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        successCount += 1;
      } catch (error) {
        toast.error(error.response?.data?.detail || `Failed to upload ${file.name}`);
      }
    }
    if (successCount > 0) {
      toast.success(`${successCount} photo${successCount > 1 ? 's' : ''} added to gallery`);
      fetchProfile();
    }
    setUploadingGallery(false);
    e.target.value = '';
  };

  const handleGalleryDelete = async (path) => {
    try {
      await axios.delete(`${API}/healers/profile/gallery-photo`, {
        params: { path },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Photo removed');
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove photo');
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const handleStartTrial = async () => {
    try {
      await axios.post(`${API}/subscription/start-trial`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Free trial started! Your profile is now live.');
      fetchSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start trial');
    }
  };

  const handleSubscribe = async (plan) => {
    setProcessingPayment(true);
    try {
      const response = await axios.post(`${API}/subscription/checkout`, {
        plan,
        origin_url: window.location.origin
      }, { headers: { Authorization: `Bearer ${token}` } });

      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setProcessingPayment(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) return;

    try {
      const response = await axios.get(`${API}/subscription/checkout-status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        toast.success('Subscription activated! Your profile is now live.');
        fetchSubscription();
        return;
      }

      if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Poll error:', error);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...profileForm,
        specialties: profileForm.specialties.split(',').map(s => s.trim()).filter(Boolean),
        languages: profileForm.languages.split(',').map(l => l.trim()).filter(Boolean),
        insurance_accepted: profileForm.insurance_accepted.split(',').map(i => i.trim()).filter(Boolean),
        hourly_rate: parseFloat(profileForm.hourly_rate),
        years_experience: parseInt(profileForm.years_experience)
      };

      if (profile) {
        await axios.put(`${API}/healers/profile`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Profile updated successfully');
      } else {
        await axios.post(`${API}/healers/profile`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Profile created successfully');
      }

      setShowProfileDialog(false);
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    }
  };

  const handleAvailabilitySubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/availability`, availabilityForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Availability added');
      setShowAvailabilityDialog(false);
      setAvailabilityForm({ date: '', start_time: '', end_time: '' });
      fetchAvailability();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add availability');
    }
  };

  const handleDeleteAvailability = async (id) => {
    if (!window.confirm('Delete this availability slot?')) return;

    try {
      await axios.delete(`${API}/availability/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Availability deleted');
      fetchAvailability();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  const upcomingBookings = bookings.filter(b => b.status === 'confirmed' || b.status === 'pending');

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
      {/* Navbar */}
      <nav className="bg-white/80 backdrop-blur-lg border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
            <Heart className="w-8 h-8 text-#8B4513" />
            <span className="text-2xl font-bold text-gray-900">Ourmend</span>
          </div>
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={logout} data-testid="logout-btn">
              Logout
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Healer Dashboard</h1>
            <p className="text-lg text-gray-600">Manage your practice and appointments</p>
          </div>
          {!profile && (
            <Button
              onClick={() => setShowProfileDialog(true)}
              className="bg-#8B4513 hover:bg-#6B3410"
              data-testid="create-profile-btn"
            >
              Create Profile
            </Button>
          )}
        </div>

        {!profile ? (
          <Card className="p-12 text-center" data-testid="no-profile-message">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Welcome to Your Healer Dashboard!</h2>
            <p className="text-gray-600 mb-6">Create your profile to start accepting bookings from patients.</p>
            <Button onClick={() => setShowProfileDialog(true)} className="bg-#8B4513 hover:bg-#6B3410" data-testid="create-profile-cta-btn">
              Create Your Profile
            </Button>
          </Card>
        ) : (
          <>
            {/* Subscription Banner */}
            {subscription && (
              <>
                {subscription.subscription_status === 'none' && subscription.is_approved && (
                  <Card className="p-6 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300 mb-6" data-testid="trial-banner">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                          <CreditCard className="w-6 h-6 text-amber-700" />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg">Start Your Free 30-Day Trial</h3>
                          <p className="text-sm text-gray-600">Your profile will go live and clients can find you!</p>
                        </div>
                      </div>
                      <Button onClick={handleStartTrial} className="bg-[#8B4513] hover:bg-[#6B3410] text-white" data-testid="start-trial-btn">
                        Start Free Trial
                      </Button>
                    </div>
                  </Card>
                )}

                {subscription.subscription_status === 'trial' && (
                  <Card className="p-4 bg-green-50 border-green-300 mb-6" data-testid="trial-active-banner">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="text-sm font-medium text-green-800">
                          Free trial active — expires {subscription.trial_end ? format(new Date(subscription.trial_end), 'PPP') : 'soon'}
                        </span>
                      </div>
                      <Button size="sm" onClick={() => document.querySelector('[data-testid="subscription-tab"]')?.click()} variant="outline" className="text-green-700 border-green-400">
                        Upgrade Now
                      </Button>
                    </div>
                  </Card>
                )}

                {subscription.subscription_status === 'expired' && (
                  <Card className="p-4 bg-red-50 border-red-300 mb-6" data-testid="expired-banner">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                        <span className="text-sm font-medium text-red-800">
                          Your subscription has expired. Your profile is hidden. Subscribe to go live again.
                        </span>
                      </div>
                      <Button size="sm" onClick={() => document.querySelector('[data-testid="subscription-tab"]')?.click()} className="bg-red-600 hover:bg-red-700 text-white">
                        Subscribe Now
                      </Button>
                    </div>
                  </Card>
                )}

                {subscription.subscription_status === 'active' && (
                  <Card className="p-4 bg-green-50 border-green-300 mb-6" data-testid="active-banner">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-sm font-medium text-green-800">
                        {subscription.subscription_plan === 'annual' ? 'Annual' : 'Monthly'} subscription active — renews {subscription.subscription_end ? format(new Date(subscription.subscription_end), 'PPP') : ''}
                      </span>
                    </div>
                  </Card>
                )}
              </>
            )}

            {/* Stats */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <Card className="p-6 bg-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm">Total Bookings</p>
                    <p className="text-3xl font-bold text-gray-900" data-testid="total-bookings">{bookings.length}</p>
                  </div>
                  <Calendar className="w-10 h-10 text-#8B4513" />
                </div>
              </Card>
              <Card className="p-6 bg-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm">Upcoming</p>
                    <p className="text-3xl font-bold text-gray-900" data-testid="upcoming-bookings">{upcomingBookings.length}</p>
                  </div>
                  <Clock className="w-10 h-10 text-emerald-600" />
                </div>
              </Card>
              <Card className="p-6 bg-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm">Hourly Rate</p>
                    <p className="text-3xl font-bold text-#8B4513" data-testid="hourly-rate">${profile.hourly_rate}</p>
                  </div>
                  <Heart className="w-10 h-10 text-rose-500" />
                </div>
              </Card>
            </div>

            {/* Tabs */}
            <Card className="p-6">
              <Tabs defaultValue="profile">
                <TabsList>
                  <TabsTrigger value="profile" data-testid="profile-tab">Profile</TabsTrigger>
                  <TabsTrigger value="availability" data-testid="availability-tab">Availability</TabsTrigger>
                  <TabsTrigger value="bookings" data-testid="bookings-tab">Bookings</TabsTrigger>
                  <TabsTrigger value="reviews" data-testid="reviews-tab">Reviews ({healerReviews.length})</TabsTrigger>
                  <TabsTrigger value="subscription" data-testid="subscription-tab">Subscription</TabsTrigger>
                </TabsList>

                <TabsContent value="profile" className="mt-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold">Your Profile</h2>
                    <Button onClick={() => setShowProfileDialog(true)} data-testid="edit-profile-btn">
                      Edit Profile
                    </Button>
                  </div>

                  {/* Profile Image */}
                  <div className="mb-6 flex items-center gap-6">
                    <div className="relative group">
                      {profile.profile_image ? (
                        <img
                          src={`${API}/files/${profile.profile_image}`}
                          alt="Profile"
                          className="w-28 h-28 rounded-full object-cover border-4 border-amber-200"
                          data-testid="profile-image"
                        />
                      ) : (
                        <div className="w-28 h-28 rounded-full bg-gradient-to-br from-amber-200 to-orange-300 flex items-center justify-center border-4 border-amber-200" data-testid="profile-image-placeholder">
                          <Camera className="w-10 h-10 text-amber-700" />
                        </div>
                      )}
                      <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer" data-testid="upload-image-label">
                        <Upload className="w-6 h-6 text-white" />
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          onChange={handleImageUpload}
                          className="hidden"
                          data-testid="profile-image-input"
                        />
                      </label>
                    </div>
                    <div>
                      <p className="font-semibold text-lg">{user?.name}</p>
                      <p className="text-sm text-gray-500">Hover over image to change photo</p>
                      {uploadingImage && <p className="text-sm text-amber-600 mt-1">Uploading...</p>}
                    </div>
                  </div>

                  {/* Photo Gallery */}
                  <div className="mb-6 pb-6 border-b border-stone-200" data-testid="gallery-section">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-lg flex items-center gap-2">
                          <ImageIcon className="w-5 h-5 text-amber-700" />
                          Photo Gallery
                        </h3>
                        <p className="text-sm text-gray-500">Show your healing space, work, or moments. Up to 10 photos.</p>
                      </div>
                      <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-white text-sm font-medium cursor-pointer ${(profile?.photos?.length || 0) >= 10 || uploadingGallery ? 'bg-stone-400 cursor-not-allowed' : 'bg-[#8B4513] hover:bg-[#6B3410]'}`} data-testid="gallery-add-label">
                        <Plus className="w-4 h-4" />
                        {uploadingGallery ? 'Uploading...' : 'Add Photos'}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          multiple
                          onChange={handleGalleryUpload}
                          disabled={(profile?.photos?.length || 0) >= 10 || uploadingGallery}
                          className="hidden"
                          data-testid="gallery-input"
                        />
                      </label>
                    </div>
                    {profile?.photos?.length > 0 ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="gallery-grid">
                        {profile.photos.map((photoPath, idx) => (
                          <div key={photoPath} className="relative group aspect-square rounded-lg overflow-hidden border border-stone-200" data-testid={`gallery-item-${idx}`}>
                            <img
                              src={`${API}/files/${photoPath}`}
                              alt={`Gallery ${idx + 1}`}
                              className="w-full h-full object-cover"
                            />
                            <button
                              type="button"
                              onClick={() => handleGalleryDelete(photoPath)}
                              className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/60 hover:bg-red-600 text-white flex items-center justify-center opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                              data-testid={`gallery-delete-${idx}`}
                              aria-label="Delete photo"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-lg border-2 border-dashed border-stone-300 p-8 text-center text-stone-500" data-testid="gallery-empty">
                        <ImageIcon className="w-10 h-10 mx-auto mb-2 text-stone-400" />
                        <p className="text-sm">No gallery photos yet. Add photos so visitors can get a feel for your practice.</p>
                      </div>
                    )}
                  </div>

                  <div className="space-y-6">
                    <div>
                      <h3 className="font-semibold mb-2">Type</h3>
                      <Badge>{profile.healer_type}</Badge>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Bio</h3>
                      <p className="text-gray-700">{profile.bio}</p>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h3 className="font-semibold mb-2">Location</h3>
                        <p className="text-gray-700">{profile.location}</p>
                      </div>
                      <div>
                        <h3 className="font-semibold mb-2">Experience</h3>
                        <p className="text-gray-700">{profile.years_experience} years</p>
                      </div>
                    </div>
                    {profile.specialties?.length > 0 && (
                      <div>
                        <h3 className="font-semibold mb-2">Specialties</h3>
                        <div className="flex flex-wrap gap-2">
                          {profile.specialties.map((s, idx) => (
                            <Badge key={idx} variant="outline">{s}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="availability" className="mt-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold">Manage Availability</h2>
                    <Button
                      onClick={() => setShowAvailabilityDialog(true)}
                      className="bg-#8B4513 hover:bg-#6B3410"
                      data-testid="add-availability-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Slot
                    </Button>
                  </div>

                  {availability.length === 0 ? (
                    <p className="text-gray-600 text-center py-8" data-testid="no-availability-message">No availability slots added yet</p>
                  ) : (
                    <div className="space-y-3">
                      {availability.map((slot) => (
                        <Card key={slot.id} className="p-4 flex items-center justify-between" data-testid={`availability-slot-${slot.id}`}>
                          <div className="flex items-center gap-4">
                            <Calendar className="w-5 h-5 text-#8B4513" />
                            <div>
                              <p className="font-semibold">{format(new Date(slot.date), 'PPP')}</p>
                              <p className="text-sm text-gray-600">{slot.start_time} - {slot.end_time}</p>
                            </div>
                            {slot.is_booked && <Badge className="bg-yellow-100 text-yellow-700">Booked</Badge>}
                          </div>
                          {!slot.is_booked && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteAvailability(slot.id)}
                              data-testid={`delete-slot-${slot.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="bookings" className="mt-6">
                  <h2 className="text-2xl font-bold mb-6">Your Bookings</h2>

                  {bookings.length === 0 ? (
                    <p className="text-gray-600 text-center py-8" data-testid="no-bookings-message">No bookings yet</p>
                  ) : (
                    <div className="space-y-4">
                      {bookings.map((booking) => (
                        <Card key={booking.id} className="p-6" data-testid={`booking-${booking.id}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-3">
                                <h3 className="text-xl font-bold text-gray-900">{booking.patient_name}</h3>
                                <Badge className={booking.status === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                                  {booking.status}
                                </Badge>
                              </div>

                              <div className="grid md:grid-cols-2 gap-3 mb-3">
                                <div className="flex items-center text-gray-600">
                                  <Calendar className="w-4 h-4 mr-2" />
                                  <span>{format(new Date(booking.date), 'PPP')}</span>
                                </div>
                                <div className="flex items-center text-gray-600">
                                  <Clock className="w-4 h-4 mr-2" />
                                  <span>{booking.start_time} - {booking.end_time}</span>
                                </div>
                              </div>

                              <p className="text-sm text-gray-600">
                                <strong>Contact:</strong> {booking.patient_email}
                              </p>
                              {booking.notes && (
                                <p className="text-sm text-gray-600 mt-2">
                                  <strong>Notes:</strong> {booking.notes}
                                </p>
                              )}
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Reviews Tab */}
                <TabsContent value="reviews" className="mt-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold">Your Reviews</h2>
                    <Button
                      variant="outline"
                      onClick={toggleReviewVisibility}
                      data-testid="toggle-reviews-btn"
                    >
                      {showReviews ? (
                        <><EyeOff className="w-4 h-4 mr-2" /> Hide Reviews on Profile</>
                      ) : (
                        <><Eye className="w-4 h-4 mr-2" /> Show Reviews on Profile</>
                      )}
                    </Button>
                  </div>

                  <Card className="p-4 mb-6 bg-amber-50 border-amber-200">
                    <p className="text-sm text-amber-800">
                      {showReviews
                        ? 'Reviews are currently visible on your public profile.'
                        : 'Reviews are currently hidden from your public profile.'}
                    </p>
                  </Card>

                  {healerReviews.length === 0 ? (
                    <p className="text-gray-600 text-center py-8" data-testid="no-reviews-message">No reviews yet</p>
                  ) : (
                    <div className="space-y-4">
                      {healerReviews.map((review) => (
                        <Card key={review.id} className="p-6" data-testid={`healer-review-${review.id}`}>
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-300 to-orange-400 flex items-center justify-center text-white font-bold text-sm">
                              {review.patient_name?.charAt(0)?.toUpperCase()}
                            </div>
                            <div className="flex-1">
                              <p className="font-semibold">{review.patient_name}</p>
                              <p className="text-xs text-gray-500">{format(new Date(review.created_at), 'PPP')}</p>
                            </div>
                            <div className="flex">
                              {[1,2,3,4,5].map(s => (
                                <Star key={s} className={`w-4 h-4 ${s <= review.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                              ))}
                            </div>
                          </div>
                          {review.tags?.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-2">
                              {review.tags.map((tag, idx) => (
                                <span key={idx} className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                          <p className="text-gray-700 text-sm">{review.text}</p>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Subscription Tab */}
                <TabsContent value="subscription" className="mt-6">
                  <h2 className="text-2xl font-bold mb-6">Subscription</h2>

                  {subscription && (
                    <div className="space-y-6">
                      {/* Current Status */}
                      <Card className="p-6">
                        <h3 className="font-bold text-lg mb-3">Current Plan</h3>
                        <div className="flex items-center gap-3">
                          <Badge className={
                            subscription.subscription_status === 'active' ? 'bg-green-100 text-green-700' :
                            subscription.subscription_status === 'trial' ? 'bg-blue-100 text-blue-700' :
                            subscription.subscription_status === 'expired' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-700'
                          } data-testid="subscription-status-badge">
                            {subscription.subscription_status === 'active' ? 'Active' :
                             subscription.subscription_status === 'trial' ? 'Free Trial' :
                             subscription.subscription_status === 'expired' ? 'Expired' : 'No Subscription'}
                          </Badge>
                          {subscription.subscription_plan && (
                            <span className="text-sm text-gray-600 capitalize">{subscription.subscription_plan} plan</span>
                          )}
                        </div>
                        {subscription.subscription_status === 'trial' && subscription.trial_end && (
                          <p className="text-sm text-gray-600 mt-2">Trial expires: {format(new Date(subscription.trial_end), 'PPP')}</p>
                        )}
                        {subscription.subscription_status === 'active' && subscription.subscription_end && (
                          <p className="text-sm text-gray-600 mt-2">Renews: {format(new Date(subscription.subscription_end), 'PPP')}</p>
                        )}
                      </Card>

                      {/* Plan Options */}
                      {(subscription.subscription_status === 'trial' || subscription.subscription_status === 'expired' || subscription.subscription_status === 'none') && (
                        <div>
                          <h3 className="font-bold text-lg mb-4">
                            {subscription.subscription_status === 'none' ? 'Choose a Plan' : 'Upgrade Your Plan'}
                          </h3>
                          <div className="grid md:grid-cols-2 gap-6">
                            {/* Monthly */}
                            <Card className="p-6 border-2 hover:border-[#8B4513] transition-colors" data-testid="monthly-plan-card">
                              <h4 className="text-xl font-bold mb-1">Monthly</h4>
                              <div className="flex items-baseline gap-1 mb-4">
                                <span className="text-4xl font-bold" style={{color: '#8B4513'}}>$19</span>
                                <span className="text-gray-500">/month</span>
                              </div>
                              <ul className="space-y-2 text-sm text-gray-700 mb-6">
                                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Profile visible to all clients</li>
                                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Receive client messages</li>
                                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Reviews & ratings</li>
                                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Cancel anytime</li>
                              </ul>
                              <Button
                                onClick={() => handleSubscribe('monthly')}
                                disabled={processingPayment}
                                className="w-full bg-[#8B4513] hover:bg-[#6B3410] text-white"
                                data-testid="subscribe-monthly-btn"
                              >
                                {processingPayment ? 'Processing...' : 'Subscribe Monthly'}
                              </Button>
                            </Card>

                            {/* Annual */}
                            <Card className="p-6 border-2 border-amber-400 relative" data-testid="annual-plan-card">
                              <Badge className="absolute -top-3 right-4 bg-amber-500 text-white">Best Value</Badge>
                              <h4 className="text-xl font-bold mb-1">Annual</h4>
                              <div className="flex items-baseline gap-1 mb-1">
                                <span className="text-4xl font-bold" style={{color: '#8B4513'}}>$190</span>
                                <span className="text-gray-500">/year</span>
                              </div>
                              <p className="text-sm text-green-600 font-medium mb-4">Save $38 vs monthly</p>
                              <ul className="space-y-2 text-sm text-gray-700 mb-6">
                                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Everything in Monthly</li>
                                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> 2 months free</li>
                                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Priority support</li>
                                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Lock in your rate</li>
                              </ul>
                              <Button
                                onClick={() => handleSubscribe('annual')}
                                disabled={processingPayment}
                                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                                data-testid="subscribe-annual-btn"
                              >
                                {processingPayment ? 'Processing...' : 'Subscribe Annually'}
                              </Button>
                            </Card>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </Card>
          </>
        )}
      </div>

      {/* Profile Dialog */}
      <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="profile-dialog">
          <DialogHeader>
            <DialogTitle>{profile ? 'Edit Profile' : 'Create Profile'}</DialogTitle>
            <DialogDescription>Complete your healer profile to start accepting bookings</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleProfileSubmit} className="space-y-4 py-4">
            <div>
              <Label htmlFor="healer_type">Healer Type *</Label>
              <Select
                value={profileForm.healer_type}
                onValueChange={(value) => setProfileForm({ ...profileForm, healer_type: value })}
              >
                <SelectTrigger data-testid="healer-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {healerTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="bio">Bio *</Label>
              <Textarea
                id="bio"
                value={profileForm.bio}
                onChange={(e) => setProfileForm({ ...profileForm, bio: e.target.value })}
                rows={4}
                required
                data-testid="bio-input"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="location">Location *</Label>
                <Input
                  id="location"
                  value={profileForm.location}
                  onChange={(e) => setProfileForm({ ...profileForm, location: e.target.value })}
                  required
                  data-testid="location-input"
                />
              </div>
              <div>
                <Label htmlFor="years_experience">Years of Experience *</Label>
                <Input
                  id="years_experience"
                  type="number"
                  value={profileForm.years_experience}
                  onChange={(e) => setProfileForm({ ...profileForm, years_experience: e.target.value })}
                  required
                  data-testid="years-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="hourly_rate">Hourly Rate ($) *</Label>
              <Input
                id="hourly_rate"
                type="number"
                step="0.01"
                value={profileForm.hourly_rate}
                onChange={(e) => setProfileForm({ ...profileForm, hourly_rate: e.target.value })}
                required
                data-testid="rate-input"
              />
            </div>

            <div>
              <Label htmlFor="specialties">Specialties (comma-separated)</Label>
              <Input
                id="specialties"
                value={profileForm.specialties}
                onChange={(e) => setProfileForm({ ...profileForm, specialties: e.target.value })}
                placeholder="e.g. Pain Management, Stress Relief"
                data-testid="specialties-input"
              />
            </div>

            <div>
              <Label htmlFor="languages">Languages (comma-separated)</Label>
              <Input
                id="languages"
                value={profileForm.languages}
                onChange={(e) => setProfileForm({ ...profileForm, languages: e.target.value })}
                placeholder="e.g. English, Spanish"
                data-testid="languages-input"
              />
            </div>

            <div>
              <Label htmlFor="insurance">Insurance Accepted (comma-separated)</Label>
              <Input
                id="insurance"
                value={profileForm.insurance_accepted}
                onChange={(e) => setProfileForm({ ...profileForm, insurance_accepted: e.target.value })}
                placeholder="e.g. Blue Cross, Aetna"
                data-testid="insurance-input"
              />
            </div>

            <div className="border-t pt-4 mt-4">
              <h3 className="font-bold text-lg mb-3">Your Story</h3>
            </div>

            <div>
              <Label htmlFor="practice_quote">Quote About Your Practice</Label>
              <Textarea
                id="practice_quote"
                value={profileForm.practice_quote}
                onChange={(e) => setProfileForm({ ...profileForm, practice_quote: e.target.value })}
                placeholder="A quote or phrase that captures your healing philosophy..."
                rows={2}
                data-testid="practice-quote-input"
              />
            </div>

            <div>
              <Label htmlFor="accolades">Accolades & Achievements</Label>
              <Textarea
                id="accolades"
                value={profileForm.accolades}
                onChange={(e) => setProfileForm({ ...profileForm, accolades: e.target.value })}
                placeholder="Certifications, awards, publications, media features, etc."
                rows={3}
                data-testid="accolades-input"
              />
            </div>

            <div className="border-t pt-4 mt-4">
              <h3 className="font-bold text-lg mb-3">Contact & Links</h3>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact_email">Contact Email</Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={profileForm.contact_email}
                  onChange={(e) => setProfileForm({ ...profileForm, contact_email: e.target.value })}
                  placeholder="hello@example.com"
                  data-testid="contact-email-input"
                />
              </div>
              <div>
                <Label htmlFor="contact_phone">Contact Phone</Label>
                <Input
                  id="contact_phone"
                  value={profileForm.contact_phone}
                  onChange={(e) => setProfileForm({ ...profileForm, contact_phone: e.target.value })}
                  placeholder="555-123-4567"
                  data-testid="contact-phone-input"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact_website">Website</Label>
                <Input
                  id="contact_website"
                  value={profileForm.contact_website}
                  onChange={(e) => setProfileForm({ ...profileForm, contact_website: e.target.value })}
                  placeholder="https://yoursite.com"
                  data-testid="contact-website-input"
                />
              </div>
              <div>
                <Label htmlFor="contact_instagram">Instagram</Label>
                <Input
                  id="contact_instagram"
                  value={profileForm.contact_instagram}
                  onChange={(e) => setProfileForm({ ...profileForm, contact_instagram: e.target.value })}
                  placeholder="@username"
                  data-testid="contact-instagram-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="blog_url">Blog URL</Label>
              <Input
                id="blog_url"
                value={profileForm.blog_url}
                onChange={(e) => setProfileForm({ ...profileForm, blog_url: e.target.value })}
                placeholder="https://yourblog.com"
                data-testid="blog-url-input"
              />
            </div>

            <Button type="submit" className="w-full bg-[#8B4513] hover:bg-[#6B3410]" data-testid="save-profile-btn">
              {profile ? 'Update Profile' : 'Create Profile'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Availability Dialog */}
      <Dialog open={showAvailabilityDialog} onOpenChange={setShowAvailabilityDialog}>
        <DialogContent data-testid="availability-dialog">
          <DialogHeader>
            <DialogTitle>Add Availability</DialogTitle>
            <DialogDescription>Set your available time slot</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleAvailabilitySubmit} className="space-y-4 py-4">
            <div>
              <Label htmlFor="date">Date</Label>
              <Input
                id="date"
                type="date"
                value={availabilityForm.date}
                onChange={(e) => setAvailabilityForm({ ...availabilityForm, date: e.target.value })}
                required
                data-testid="date-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start_time">Start Time</Label>
                <Input
                  id="start_time"
                  type="time"
                  value={availabilityForm.start_time}
                  onChange={(e) => setAvailabilityForm({ ...availabilityForm, start_time: e.target.value })}
                  required
                  data-testid="start-time-input"
                />
              </div>
              <div>
                <Label htmlFor="end_time">End Time</Label>
                <Input
                  id="end_time"
                  type="time"
                  value={availabilityForm.end_time}
                  onChange={(e) => setAvailabilityForm({ ...availabilityForm, end_time: e.target.value })}
                  required
                  data-testid="end-time-input"
                />
              </div>
            </div>

            <Button type="submit" className="w-full bg-#8B4513 hover:bg-#6B3410" data-testid="submit-availability-btn">
              Add Availability
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HealerDashboard;